import { useState } from 'react';
import { X, Search, ArrowRight } from 'lucide-react';
import type { BoardProject } from '@/hooks/use-staffing-board';

interface TouchAssignPickerProps {
  techName: string;
  techId: number;
  assignmentId?: number;
  projects: BoardProject[];
  onAssign: (techId: number, assignmentId: number | undefined, targetProjectId: number | null) => void;
  onClose: () => void;
}

export function TouchAssignPicker({
  techName,
  techId,
  assignmentId,
  projects,
  onAssign,
  onClose,
}: TouchAssignPickerProps) {
  const [search, setSearch] = useState('');

  const filtered = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="fixed inset-0 z-50 print:hidden" data-testid="touch-assign-picker">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="absolute bottom-0 left-0 right-0 max-h-[70vh] rounded-t-2xl bg-white shadow-xl flex flex-col"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)' }}
      >
        {/* Handle + header */}
        <div className="px-4 pt-3 pb-2 border-b border-gray-100 flex-shrink-0">
          <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300" />
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold text-gray-900">
              Assign {techName}
            </h3>
            <button
              onClick={onClose}
              className="p-2 -mr-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        {/* Project list */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {/* "Unassign" option when the tech is already assigned */}
          {assignmentId != null && (
            <button
              onClick={() => {
                onAssign(techId, assignmentId, null);
                onClose();
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
              data-testid="touch-assign-unassign"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-500 flex-shrink-0">
                <X className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium text-gray-700">Move to Bench (unassign)</span>
            </button>
          )}

          {filtered.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-400">No projects found</p>
          )}

          {filtered.map((project) => (
            <button
              key={project.id}
              onClick={() => {
                onAssign(techId, assignmentId, project.id);
                onClose();
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left hover:bg-blue-50 active:bg-blue-100 transition-colors"
              data-testid={`touch-assign-project-${project.id}`}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600 flex-shrink-0">
                <ArrowRight className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{project.name}</p>
                {project.location_fk_name && (
                  <p className="text-xs text-gray-500 truncate">{project.location_fk_name}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
